import {Separator} from 'platform/components';
import {
  Box,
  Clickable,
  Heading,
  Hide,
  HStack,
  Icon,
  Show,
  Space,
  Text,
  ThemeIconKey,
  VStack,
} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {buildArray, suffixTestId, TestIdProps} from 'shared/web';

import {CatalogueType} from '../../../hooks/useCatalogue';
import {useVehicleTranslatedInfo} from '../../../hooks/useVehicleTranslatedInfo';
import i18n from '../../../i18n/i18n';
import {AvailableCar} from '../../../types/AvailableCar';
import {PriceReportType} from '../../../types/PriceReportType';
import {powerFormatter} from '../../../utils/powerFormatter';
import {openAdLink} from '../utils/openAdLink';
import {FeatureList} from './FeatureList';
import {VehicleCardContentAdvertisementStatus} from './VehicleCardContentAdvertisementStatus';
import {VehicleCardContentFeatureScore} from './VehicleCardContentFeatureScore';
import {VehicleCardContentMileage} from './VehicleCardContentMileage';
import {VehicleCardContentPrice} from './VehicleCardContentPrice';
import {VehicleCardContentYear} from './VehicleCardContentYear';

interface RowHeaderProps extends TestIdProps {
  priceReport: PriceReportType;
  catalogue: CatalogueType;
  vehicle: AvailableCar;
  isMyVehicle: boolean;
  isMyAd: boolean;
}

export function VehicleCardContent(props: RowHeaderProps) {
  const {getTransmission, getSellerType, getFuelType, getDrive, getCarStyle} = props.catalogue;
  const {data: vehicleTranslatedInfo} = useVehicleTranslatedInfo(
    isNotNil(props.vehicle.model)
      ? {...props.priceReport?.vehicle, modelFamily: props.vehicle.model}
      : props.priceReport?.vehicle
  );

  const vehicleDetailValues = buildArray<{icon: ThemeIconKey; text: string | number}>([
    {
      icon: 'automotive/engine',
      text: powerFormatter(props.vehicle.maxPower ?? props.vehicle.power),
    },
    {icon: 'automotive/differential', text: getDrive(props.vehicle.drive_type)},
    {icon: 'automotive/transmission', text: getTransmission(props.vehicle.transmission)},
    {icon: 'automotive/fuel_type', text: getFuelType(props.vehicle.fuel_type)},
    {icon: 'automotive/car', text: getCarStyle(props.vehicle.car_style)},
  ]).whenNot(props.isMyVehicle || isNil(props.vehicle.seller?.type), {
    icon: 'automotive/location',
    text: getSellerType(props.vehicle.seller?.type),
  });

  const vehicleName = match([
    props.vehicle.ad_title,
    props.isMyVehicle,
    props.priceReport.vehicle.vehicleTitle,
  ])
    .with([Pattern.nonNullable, false, Pattern.any], () => props.vehicle.ad_title)
    .with([Pattern.any, true, Pattern.nonNullable], () => props.priceReport.vehicle.vehicleTitle)
    .otherwise(() => `${vehicleTranslatedInfo.make} ${vehicleTranslatedInfo.model}`);

  const handleTitleClick = () => {
    if (!props.vehicle.is_sold && isNotNil(props.vehicle.url)) {
      openAdLink(props.vehicle.url, props.priceReport.market, i18n.language);
    }
  };

  return (
    <HStack wrap spacing={3} justify="space-between">
      <VStack spacing={3} minWidth={0}>
        <Box>
          <Clickable
            onClick={handleTitleClick}
            isDisabled={!!props.vehicle.is_sold || props.isMyVehicle}
          >
            <HStack align="center">
              <Heading size={4} data-testid={suffixTestId('label', props)}>
                {vehicleName}
              </Heading>
              <Hide when={props.vehicle.is_sold || props.isMyVehicle}>
                <Space horizontal={2} />
                <Icon value="action/open_in_new" size={4} />
              </Hide>
            </HStack>
          </Clickable>
          <Space vertical={1} />
          <HStack spacing={4} wrap>
            {vehicleDetailValues.map((detail) => (
              <HStack key={detail.text}>
                <Icon value={detail.icon} size={4} />
                <Space horizontal={2} />
                <Text
                  inline
                  size="xSmall"
                  color="secondary"
                  alternative
                  noWrap
                  data-testid={suffixTestId('details', props)}
                >
                  {detail.text}
                </Text>
              </HStack>
            ))}
          </HStack>
        </Box>
        <FeatureList
          firstWhite
          values={props.vehicle.highlight_features}
          isBold={false}
          isRow
          spacing={2}
          textColor="primary"
          isSortable
          isMyVehicle={props.isMyVehicle}
          isMyAd={props.isMyAd}
          data-testid={suffixTestId('columnHeader', props)}
        />
      </VStack>
      <Separator spacing={0} />

      <Hide onMobile>
        <HStack grow={1} align="flex-end" wrap>
          <VehicleCardContentAdvertisementStatus
            vehicle={props.vehicle}
            priceReport={props.priceReport}
            isMyVehicle={props.isMyVehicle}
            data-testid={suffixTestId('advertisement-status', props)}
          />
          <VehicleCardContentYear
            vehicle={props.vehicle}
            priceReport={props.priceReport}
            isMyVehicle={props.isMyVehicle}
            data-testid={suffixTestId('year', props)}
          />
          <VehicleCardContentFeatureScore
            vehicle={props.vehicle}
            priceReport={props.priceReport}
            isMyVehicle={props.isMyVehicle}
            data-testid={suffixTestId('feature-score', props)}
          />
          <VehicleCardContentMileage
            vehicle={props.vehicle}
            priceReport={props.priceReport}
            isMyVehicle={props.isMyVehicle}
            data-testid={suffixTestId('mileage', props)}
          />
          <VehicleCardContentPrice
            vehicle={props.vehicle}
            priceReport={props.priceReport}
            isMyVehicle={props.isMyVehicle}
            data-testid={suffixTestId('price', props)}
          />
        </HStack>
      </Hide>

      <Show onMobile>
        <Box flex={1}>
          <VStack spacing={3}>
            <VehicleCardContentYear
              vehicle={props.vehicle}
              priceReport={props.priceReport}
              isMyVehicle={props.isMyVehicle}
            />
            <VehicleCardContentFeatureScore
              vehicle={props.vehicle}
              priceReport={props.priceReport}
              isMyVehicle={props.isMyVehicle}
            />
            <VehicleCardContentMileage
              vehicle={props.vehicle}
              priceReport={props.priceReport}
              isMyVehicle={props.isMyVehicle}
            />
            <VehicleCardContentAdvertisementStatus
              vehicle={props.vehicle}
              priceReport={props.priceReport}
              isMyVehicle={props.isMyVehicle}
            />
            <VehicleCardContentPrice
              vehicle={props.vehicle}
              priceReport={props.priceReport}
              isMyVehicle={props.isMyVehicle}
            />
          </VStack>
        </Box>
      </Show>
    </HStack>
  );
}
